import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { ChevronLeft, Mail } from 'react-feather';
import {
  attendanceFollowUpsApi,
  type AbsenceEmailTemplate,
  type AttendanceFollowUpEntry,
  type FollowUpParticipantDetailResponse,
} from '../../../../services/api/attendanceFollowUps';
import { FollowUpAccordionRow } from './FollowUpAccordionRow';
import { SkipWithNoteModal } from './SkipWithNoteModal';

interface FollowUpParticipantDetailProps {
  cohortId: string;
  cohortParticipantId: string;
  onBack: () => void;
  templates: AbsenceEmailTemplate[];
}

export function FollowUpParticipantDetail({
  cohortId,
  cohortParticipantId,
  onBack,
  templates,
}: FollowUpParticipantDetailProps) {
  const [data, setData] = useState<FollowUpParticipantDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [skipModalEntry, setSkipModalEntry] = useState<AttendanceFollowUpEntry | null>(null);
  const [sending, setSending] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await attendanceFollowUpsApi.getParticipantFollowUps(
        cohortId,
        cohortParticipantId,
      );
      setData(result);
      // Default-expand the first pending entry if any.
      const firstPending = result.entries.find(
        e => e.status === 'pending' || e.status === 'no_template',
      );
      setExpandedEntryId(prev => prev ?? firstPending?.id ?? null);
      // Drop selections for entries that no longer exist / are no longer pending.
      setSelectedIds(prev => {
        const keep = new Set<string>();
        for (const e of result.entries) {
          if (
            (e.status === 'pending' || e.status === 'no_template') &&
            prev.has(e.id)
          ) {
            keep.add(e.id);
          }
        }
        return keep;
      });
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load participant follow-ups',
        color: 'red',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  }, [cohortId, cohortParticipantId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleToggleExpand = (entryId: string) => {
    setExpandedEntryId(prev => (prev === entryId ? null : entryId));
  };

  const handleCheckboxToggle = (entryId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(entryId);
      else next.delete(entryId);
      return next;
    });
  };

  const handleSaveEdits = async (
    entry: AttendanceFollowUpEntry,
    edits: { rendered_subject: string; rendered_body: string },
  ) => {
    try {
      await attendanceFollowUpsApi.updateEntry(cohortId, entry.id, edits);
      notifications.show({
        title: 'Saved',
        message: 'Email content updated',
        color: 'green',
      });
      await refresh();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save edits', color: 'red' });
    }
  };

  const handleTemplateOverride = async (entry: AttendanceFollowUpEntry, templateId: string) => {
    try {
      await attendanceFollowUpsApi.updateEntry(cohortId, entry.id, {
        override_template_id: templateId,
      });
      await refresh();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Could not re-render with that template',
        color: 'red',
      });
    }
  };

  const handleConfirmSkip = async (entry: AttendanceFollowUpEntry, note: string) => {
    try {
      await attendanceFollowUpsApi.updateEntry(cohortId, entry.id, {
        status: 'skipped',
        skip_note: note.length > 0 ? note : null,
      });
      notifications.show({ title: 'Skipped', message: 'Follow-up marked as skipped', color: 'gray' });
      await refresh();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to skip follow-up',
        color: 'red',
      });
    }
  };

  const sendSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    modals.openConfirmModal({
      title: 'Send Attendance Follow-Up Emails',
      zIndex: 400,
      children: (
        <Text size="sm">
          Send {ids.length} follow-up email{ids.length !== 1 ? 's' : ''}? Each will CC the coach and
          use your email as reply-to.
        </Text>
      ),
      labels: {
        confirm: `Send ${ids.length} Email${ids.length !== 1 ? 's' : ''}`,
        cancel: 'Cancel',
      },
      confirmProps: { color: 'medleyGreen' },
      onConfirm: async () => {
        setSending(true);
        try {
          const result = await attendanceFollowUpsApi.sendBatch(cohortId, { entryIds: ids });
          notifications.show({
            title: 'Emails Sent',
            message: `${result.sent_count} sent, ${result.failed_count} failed`,
            color: result.failed_count > 0 ? 'yellow' : 'green',
          });
          await refresh();
        } catch {
          notifications.show({
            title: 'Error',
            message: 'Failed to send batch',
            color: 'red',
          });
        } finally {
          setSending(false);
        }
      },
    });
  };

  const participantInitials = useMemo(() => getInitials(data?.participant_name ?? ''), [data]);
  const totalCount = (data?.pending_count ?? 0) + (data?.past_count ?? 0);

  if (loading && !data) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  if (!data) return null;

  return (
    <Stack gap="sm" p="md" pb={selectedIds.size > 0 ? 80 : 'md'}>
      <UnstyledButton onClick={onBack}>
        <Group gap={4} c="dimmed">
          <ChevronLeft size={14} />
          <Text size="sm">All Follow-Ups</Text>
        </Group>
      </UnstyledButton>

      <Group gap="sm" wrap="nowrap">
        <Avatar size={48} color="medleyGreen" radius="xl">
          {participantInitials}
        </Avatar>
        <Box style={{ flex: 1 }}>
          <Text fw={700} size="lg">
            {data.participant_name}
          </Text>
          <Text size="xs" c="dimmed">
            {data.group_name}
            {data.coach_name ? ` · Coach ${data.coach_name}` : ''}
            {' · '}
            {totalCount} follow-up{totalCount !== 1 ? 's' : ''} in this cohort
          </Text>
        </Box>
        {data.pending_count > 0 && (
          <Badge
            size="sm"
            variant="filled"
            styles={{
              root: {
                background: 'var(--medley-flag-bg)',
                color: 'var(--medley-flag-text)',
              },
            }}
          >
            PENDING ACTION
          </Badge>
        )}
      </Group>

      <Stack gap="xs">
        {data.entries.map(entry => (
          <FollowUpAccordionRow
            key={entry.id}
            entry={entry}
            expanded={expandedEntryId === entry.id}
            onToggle={() => handleToggleExpand(entry.id)}
            onSaveEdits={edits => handleSaveEdits(entry, edits)}
            onSkipClick={() => setSkipModalEntry(entry)}
            onTemplateOverride={id => handleTemplateOverride(entry, id)}
            onCheckboxToggle={handleCheckboxToggle}
            checked={selectedIds.has(entry.id)}
            templates={templates}
          />
        ))}
      </Stack>

      {selectedIds.size > 0 && (
        <Box
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#FFFFFF',
            borderTop: '1px solid #E8E6E1',
            padding: '12px 16px',
            marginLeft: -16,
            marginRight: -16,
            marginBottom: -16,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            color="medleyGreen"
            leftSection={<Mail size={14} />}
            loading={sending}
            onClick={sendSelected}
          >
            Send All Selected ({selectedIds.size})
          </Button>
        </Box>
      )}

      <SkipWithNoteModal
        opened={!!skipModalEntry}
        onClose={() => setSkipModalEntry(null)}
        onConfirm={note => {
          if (skipModalEntry) return handleConfirmSkip(skipModalEntry, note);
        }}
        participantName={data.participant_name}
        sessionNumber={skipModalEntry?.session_number}
      />
    </Stack>
  );
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase() || '?';
}

export default FollowUpParticipantDetail;
